<template>
    <div class="admin-articles-list-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
            @click:row="onRowClick"
        >
            <template #loading>
                <div class="admin-articles-list-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="kasanowa-top-filter-wrapper">
                    <KTextField
                        id="search"
                        v-model="search"
                        name="search"
                        label=""
                        :placeholder="$t('common.search')"
                        hide-details
                        clearable
                    />
                </div>
            </template>

            <template #item.title="elem">
                <div v-if="elem.item.name != null">{{ elem.item.name }}</div>
            </template>

            <template #item.created_at="elem">
                <div v-if="elem.item.created_at != null">{{ useDataFormat(elem.item.created_at) }}</div>
            </template>

            <template #item.actions="elem">
                <div class="admin-articles-list-table__action-wrapper" @click.stop>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover admin-articles-list-table__icon-wrapper admin-articles-list-table__icon-wrapper--edit" @click="navigateTo(`/articles/${elem.item.id}`)">
                                <KIcon icon="edit" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('articles.edit') }}</div>
                        </template>
                    </KTooltip>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover admin-articles-list-table__icon-wrapper admin-articles-list-table__icon-wrapper--trash" @click="openDeleteWarehouseDialog(elem.item.id)">
                                <KIcon icon="trash" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('articles.delete') }}</div>
                        </template>
                    </KTooltip>
                    <KTooltip without-btn>
                        <template v-slot:title>
                            <div class="kasanowa-hover admin-articles-list-table__icon-wrapper" @click="navigateTo(`/articles/preview/${elem.item.id}`)">
                                <KIcon icon="eye" :color="grey400" />
                            </div>
                        </template>
                        <template v-slot:content>
                            <div>{{ $t('articles.preview') }}</div>
                        </template>
                    </KTooltip>
                </div>
            </template>

            <template #footer.prepend>
                <div class="kasanowa-total-table-counter">{{ $t('common.total') }}: {{ total }}</div>
            </template>
        </v-data-table-server>

        <KDialog
            :is-open-dialog-action="isOpenDeleteDialog"
            :is-close-dialog-action="isCloseDeleteDialog"
            close-header-btn
            @close-dialog="closeDialogWhenNoTargetPressed"
        >
            <template v-slot:default>
                <div>
                    <div class="admin-articles-list-table__dialog-title">{{ $t('articles.areYouSureDeleteWarehouse') }}</div>
                    <div class="admin-articles-list-table__btn-dialog-wrapper">
                        <KButton
                            id="articles-cancel"
                            :label="$t('articles.cancel')"
                            secondary
                            @click="closeDialogWhenNoTargetPressed"
                        />
                        <KButton
                            id="articles-delete"
                            :label="$t('articles.deleteBtn')"
                            type="submit"
                            secondary
                            :loading="loadingDeleteBtn"
                            @click="deleteAction"
                        />
                    </div>
                </div>
            </template>
        </KDialog>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import ArticleApi from '@/api/v1/ArticleApi';
import { toast } from 'vue3-toastify';
import { navigateTo } from '@/hooks/NavigateHooks';
import { useDataFormat } from '@/hooks/DataHooks';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { IArticleResponse } from '@/models/ArticleModel';
import KDialog from '@/components/atoms/KDialog.vue';
import KIcon from '@/components/atoms/KIcon.vue';
import KTooltip from '@/components/atoms/KTooltip.vue';
import KButton from '@/components/atoms/KButton.vue';
import KTextField from '@/components/atoms/KTextField.vue';

const { grey400 } = variables;

const headers = ref<ITableHeader[]>([
    {
        title: t('articles.title'),
        align: 'start',
        sortable: false,
        key: 'title',
        minWidth: '300px',
    },
    {
        title: t('articles.createdAt'),
        align: 'center',
        sortable: false,
        key: 'created_at',
        width: '',
    },
    {
        title: '',
        align: 'center',
        sortable: false,
        key: 'actions',
        width: '150px',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<IArticleResponse[]>([]);
const articleApi = new ArticleApi();
const articleList = async() => {
    loading.value = true;
    try {
        const result = await articleApi.articleList(
            options.value.page,
            options.value.itemsPerPage,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
            isAdmin.value || isAdminColian.value,
            search.value,
        );
        if (!result.isSuccess) {
            toast.error(t('articles.toast.errorArticleList'));
            loading.value = false;
            return;
        }
        currentPage.value = result.payload.current_page;
        lastPage.value = result.payload.last_page;
        total.value = result.payload.total;
        items.value = result.payload.data;
    } catch (e) {
        toast.error(t('articles.toast.errorArticleList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    articleList();
};

const isOpenDeleteDialog = ref<boolean>(false);
const isCloseDeleteDialog = ref<boolean>(true);
const selectedArticleId = ref<number | null>(null);
const closeDialogWhenNoTargetPressed = () => {
    isOpenDeleteDialog.value = false;
    isCloseDeleteDialog.value = true;
    selectedArticleId.value = null;
};
const openDeleteWarehouseDialog = (id: number) => {
    selectedArticleId.value = id;
    isOpenDeleteDialog.value = true;
    isCloseDeleteDialog.value = false;
};
const loadingDeleteBtn = ref<boolean>(false);
const { isAdmin, isAdminColian } = storeToRefs(useUserStore());
const deleteAction = async() => {
    loadingDeleteBtn.value = true;
    try {
        const result = await articleApi.deleteArticle(selectedArticleId.value, isAdmin.value || isAdminColian.value);
        if (!result.isSuccess) {
            toast.error(t('articles.toast.errorArticleDeleteAction'));
            loadingDeleteBtn.value = false;
            closeDialogWhenNoTargetPressed();
            return;
        }
        toast.success(t('articles.toast.successArticleDeleteAction'));
        closeDialogWhenNoTargetPressed();
        articleList();
    } catch (e) {
        toast.error(t('articles.toast.errorArticleDeleteAction'));
        loadingDeleteBtn.value = false;
        closeDialogWhenNoTargetPressed();
        return;
    }
    loadingDeleteBtn.value = false;
};

const onRowClick = (event: any, item: any) => {
    navigateTo(`/articles/${item.item.id}`);
};

const search = ref<string | null>(null);
const searchInterval = ref<any>(undefined);
watch(() => search.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        clearTimeout(searchInterval.value);
    }
    searchInterval.value = setTimeout(() => {
        articleList();
    }, 700);
}, {
    deep: true
});
</script>

<style scoped lang="scss">
.admin-articles-list-table {
    margin-top: 20px;

    &__loading {
        opacity: 0.6;
    }

    &__action-wrapper {
        display: flex;
        justify-content: space-around;
    }

    &__icon-wrapper {
        padding: 5px;
        height: 26px;
    }

    &__btn-dialog-wrapper {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @include media-breakpoint-up(sm) {
            margin-top: 20px;
            flex-direction: row;
            width: 100%;
            justify-content: space-around;
        }
    }

    &__dialog-title {
        font-size: 18px;
        letter-spacing: 0;
    }

    &:deep(.v-data-table tr:hover) {
        .admin-articles-list-table__icon-wrapper--edit svg {
            fill: $primary-800;
        }

        .admin-articles-list-table__icon-wrapper--trash svg {
            fill: $red-400;
        }
    }
}
</style>
